<template>
    <main class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-y-auto" tabindex="0">
        <form @submit.prevent="saveModuleType">
            <div class="px-8 sm:px-8 lg:px-8 pt-6 pb-2">
                <div class="w-full flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                    <div class="flex-1 min-w-0">
                        <h1 class="stage-page-title">{{ isEdit ? $t('Content-Modul bearbeiten') : $t('Content-Modul anlegen') }}</h1>
                    </div>
                    <div class="inline-flex items-center">
                        <div
                            v-if="isEdit"
                            ref="headerActionMenuWrapper"
                            class="relative inline-flex items-stretch mr-0"
                        >
                            <button class="btn-stage-primary !rounded-r-none !pr-3.5 inline-flex items-center gap-1.5" type="submit">
                                <span>{{ $t('Save') }}</span>
                                <span
                                    class="h-2 w-2 flex-shrink-0 rounded-full"
                                    :class="[publicationStateDotClass, showPublicationStateDot ? '' : 'opacity-0']"
                                    :title="publicationStateLabel"
                                    :aria-label="publicationStateLabel"
                                ></span>
                            </button>
                            <button
                                ref="headerActionMenuTrigger"
                                class="btn-stage-primary !rounded-l-none border-l border-l-[rgba(255,255,255,0.16)] min-w-[2.25rem] !px-2 inline-flex items-center justify-center"
                                type="button"
                                :title="$t('Aktionen')"
                                :aria-label="$t('Aktionen')"
                                :aria-expanded="headerActionMenuOpen ? 'true' : 'false'"
                                @click.stop="toggleHeaderActionMenu"
                            >
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                            </button>
                            <div
                                v-show="headerActionMenuOpen"
                                ref="headerActionMenu"
                                class="absolute right-0 top-full z-20 mt-2 whitespace-nowrap rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
                            >
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('publish')">
                                    {{ $t('Veröffentlichen') }}
                                </button>
                                <button v-if="moduleType.published_at" class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('reset')">
                                    {{ $t('Zurücksetzen (letzte Veröffentlichung)') }}
                                </button>
                                <button class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('cancel')">
                                    {{ $t('Abbrechen') }}
                                </button>
                                <button v-if="!moduleType.is_system" class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" type="button" @click.stop="runHeaderAction('delete')">
                                    {{ $t('Preset löschen') }}
                                </button>
                            </div>
                        </div>
                        <button v-else class="btn-stage-primary" type="submit">
                            {{ $t('Preset anlegen') }}
                        </button>
                    </div>
                </div>
            </div>

            <div class="stage-page-body">
                <loading :status="loading"/>

                <div class="space-y-6">
                    <app-panel :title="$t('Allgemein')">
                            <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Key') }}</label>
                                    <input v-model="moduleType.key" class="stage-filter-input form-input block w-full text-sm" :readonly="moduleType.is_system" required>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Name') }}</label>
                                    <input v-model="moduleType.name" class="stage-filter-input form-input block w-full text-sm" required>
                                </div>
                                <div>
                                    <label class="stage-filter-label block mb-2">{{ $t('Font Awesome Icon') }}</label>
                                    <input v-model="moduleType.icon" class="stage-filter-input form-input block w-full text-sm">
                                </div>
                            </div>

                            <div class="mt-6">
                                <label class="stage-filter-label block mb-2">{{ $t('Beschreibung') }}</label>
                                <textarea v-model="moduleType.description" rows="3" class="stage-filter-input form-textarea block w-full text-sm"></textarea>
                            </div>
                    </app-panel>

                    <app-panel :title="$t('HTML (Sprachvarianten)')" body-class="cms-translation-panel-body">
                            <div v-if="moduleType.translations.length > 0" class="flex flex-col">
                                <div class="flex flex-wrap items-center justify-between gap-4 border-b border-gray-100 px-5">
                                    <div class="flex items-center">
                                        <button
                                            v-for="(translation, index) in moduleType.translations"
                                            :key="translation.cms_language_id"
                                            type="button"
                                            class="-mb-px appearance-none border-0 border-b-2 bg-transparent px-3.5 pb-3 pt-3.5 text-sm font-medium leading-5 transition-colors duration-150 hover:text-gray-700"
                                            :class="activeTranslationIndex === index ? 'border-b-gray-700 text-gray-800' : 'border-b-transparent text-gray-500'"
                                            @click="activeTranslationIndex = index"
                                        >
                                            {{ translationLabel(translation, index) }}
                                        </button>
                                    </div>
                                    <button
                                        class="inline-flex cursor-pointer items-center justify-center text-gray-400 transition hover:text-gray-700"
                                        type="button"
                                        :title="$t('Datei einfügen')"
                                        :aria-label="$t('Datei einfügen')"
                                        @click="openModuleTypeHtmlMediaDrawer"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.image"></svg-vue>
                                    </button>
                                </div>

                                <div v-if="activeTranslation" class="cms-translation-content">
                                    <div class="cms-translation-html-layout">
                                        <div class="cms-translation-html-editor">
                                            <cms-html-code-editor ref="moduleTypeHtmlEditor" v-model="activeTranslation.module_html" :rows="22" variant="vault" fill />
                                        </div>
                                        <div class="cms-variable-column">
                                            <div class="cms-variable-column-body">
                                                <div class="max-h-[34rem] overflow-y-auto">
                                                    <div class="cms-variable-section">
                                                        <div class="cms-variable-section-title">{{ $t('Slots') }}</div>
                                                        <div v-if="moduleSlotDefinitions.length > 0">
                                                            <div
                                                                v-for="(slot, index) in moduleSlotDefinitions"
                                                                :key="slot.key || ('slot-' + index)"
                                                                class="cms-variable-row text-xs"
                                                            >
                                                                <div class="flex items-start justify-between gap-3">
                                                                    <div class="min-w-0">
                                                                        <div class="font-medium text-gray-900">{{ slot.label }}</div>
                                                                        <div class="font-mono text-[11px] text-gray-500 mt-1">{{ slot.key }}</div>
                                                                    </div>
                                                                    <div v-if="slot.max_items" class="cms-slot-badge">
                                                                        {{ $t('max') }} {{ slot.max_items }}
                                                                    </div>
                                                                </div>
                                                                <div v-if="slot.description" class="text-gray-500 mt-2">{{ slot.description }}</div>
                                                            </div>
                                                        </div>
                                                        <div v-else class="cms-variable-empty">
                                                            {{ $t('Noch keine Child-Slots im Slot-Schema definiert.') }}
                                                        </div>
                                                    </div>

                                                    <div class="cms-variable-section">
                                                        <div class="cms-variable-section-title">{{ $t('Platzhalter') }}</div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ value:field_key }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Gibt den gespeicherten Feldwert aus.') }}</div>
                                                        </div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ text:field_key }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Gibt reinen Text HTML-escaped mit Zeilenumbrüchen aus.') }}</div>
                                                        </div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ html:field_key }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Gibt HTML-Felder ohne weitere Umwandlung aus.') }}</div>
                                                        </div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ file:field_key:url }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Liest die öffentliche Datei-URL aus einem Datei-Picker-Feld aus.') }}</div>
                                                        </div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ file:field_key:alt }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Liest den sprachabhängigen Alt-Text der Datei aus.') }}</div>
                                                        </div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ file:field_key:title }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Liest den sprachabhängigen Titel-Text der Datei aus.') }}</div>
                                                        </div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ file:field_key:caption }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Liest die sprachabhaengige Bildunterschrift der Datei aus.') }}</div>
                                                        </div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ file:field_key:source }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Liest die sprachabhaengige Quelle der Datei aus.') }}</div>
                                                        </div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ file:field_key:copyright }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Liest den sprachabhängigen Copyright-Hinweis der Datei aus.') }}</div>
                                                        </div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ file:field_key:width }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Liest die gespeicherte Bildbreite der Datei aus.') }}</div>
                                                        </div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ file:field_key:height }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Liest die gespeicherte Bildhöhe der Datei aus.') }}</div>
                                                        </div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ slot:slot_key }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Rendert den verschachtelten Slot des Moduls.') }}</div>
                                                        </div>
                                                        <div class="cms-variable-row text-xs">
                                                            <div class="font-mono text-gray-900" v-pre>{{ if:field_key }}...{{ /if:field_key }}</div>
                                                            <div class="text-gray-500 mt-1">{{ $t('Zeigt einen Bereich nur bei befülltem oder aktivem Feld.') }}</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                    </app-panel>

                    <app-panel :title="$t('Schemas')" body-class="cms-code-panel-body">
                        <template #header-actions>
                            <div class="text-xs text-gray-500">{{ $t('JSON-Definitionen für Formularfelder und Child-Slots') }}</div>
                        </template>
                            <div class="cms-schema-layout">
                                <div class="cms-code-panel-shell">
                                    <div class="flex flex-wrap items-center gap-0 border-b border-gray-100 bg-white px-5">
                                        <button
                                            v-for="tab in schemaTabs"
                                            :key="tab.key"
                                            type="button"
                                            class="-mb-px appearance-none border-0 border-b-2 bg-transparent px-3.5 pb-3 pt-3.5 text-sm font-medium leading-5 transition-colors duration-150 hover:text-gray-700"
                                            :class="activeSchemaTab === tab.key ? 'border-b-gray-700 text-gray-800' : 'border-b-transparent text-gray-500'"
                                            @click="activeSchemaTab = tab.key"
                                        >
                                            {{ tab.label }}
                                        </button>
                                    </div>

                                    <div v-show="activeSchemaTab === 'settings_schema'" class="cms-code-panel-fill">
                                        <html-code-editor v-model="schemaEditors.settings_schema" :rows="18" variant="vault" fill />
                                    </div>
                                    <div v-show="activeSchemaTab === 'translation_schema'" class="cms-code-panel-fill">
                                        <html-code-editor v-model="schemaEditors.translation_schema" :rows="18" variant="vault" fill />
                                    </div>
                                    <div v-show="activeSchemaTab === 'slot_schema'" class="cms-code-panel-fill">
                                        <html-code-editor v-model="schemaEditors.slot_schema" :rows="18" variant="vault" fill />
                                    </div>
                                </div>

                                <div class="cms-schema-sidebar">
                                    <div class="cms-schema-sidebar-body">
                                        <div class="cms-variable-section">
                                            <div class="cms-variable-section-title">{{ activeSchemaInfo.title }}</div>
                                            <div class="cms-schema-sidebar-text">
                                                {{ activeSchemaInfo.description }}
                                            </div>
                                        </div>

                                        <div class="cms-variable-section">
                                            <div class="cms-variable-section-title">{{ $t('Enthaltene Angaben') }}</div>
                                            <div
                                                v-for="item in activeSchemaInfo.items"
                                                :key="activeSchemaTab + '-' + item.title"
                                                class="cms-variable-row text-xs"
                                            >
                                                <div class="font-medium text-gray-900">{{ item.title }}</div>
                                                <div class="text-gray-500 mt-1">{{ item.text }}</div>
                                            </div>
                                        </div>

                                        <div class="cms-variable-section">
                                            <div class="cms-variable-section-title">{{ $t('Hinweis') }}</div>
                                            <div class="cms-schema-sidebar-text">
                                                {{ activeSchemaInfo.note }}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                    </app-panel>

                    <app-panel :title="$t('Code')" panel-class="self-start" body-class="cms-code-panel-body">
                            <div class="cms-code-panel-shell">
                                <div class="flex flex-wrap items-center gap-0 border-b border-gray-100 bg-white px-5">
                                    <button
                                        v-for="tab in codeTabs"
                                        :key="tab.key"
                                        type="button"
                                        class="-mb-px appearance-none border-0 border-b-2 bg-transparent px-3.5 pb-3 pt-3.5 text-sm font-medium leading-5 transition-colors duration-150 hover:text-gray-700"
                                        :class="activeCodeTab === tab.key ? 'border-b-gray-700 text-gray-800' : 'border-b-transparent text-gray-500'"
                                        @click="activeCodeTab = tab.key"
                                    >
                                        {{ tab.label }}
                                    </button>
                                </div>

                                <div v-show="activeCodeTab === 'module_css'" class="cms-code-panel-fill">
                                    <html-code-editor v-model="moduleType.module_css" :rows="18" variant="vault" fill />
                                </div>
                                <div v-show="activeCodeTab === 'module_js'" class="cms-code-panel-fill">
                                    <html-code-editor v-model="moduleType.module_js" :rows="18" variant="vault" fill />
                                </div>
                            </div>
                    </app-panel>

                    <app-panel :title="$t('Custom-Field Typen')">
                            <div v-if="customFieldTypes.length > 0" class="cms-reference-card-grid">
                                <div
                                    v-for="fieldType in customFieldTypes"
                                    :key="fieldType.key"
                                    class="cms-reference-card"
                                >
                                    <div class="cms-reference-card-head">
                                        <div>
                                            <div class="cms-reference-card-title">{{ fieldType.name }}</div>
                                            <div class="cms-reference-card-key">{{ fieldType.key }}</div>
                                        </div>
                                        <div class="cms-reference-card-badge">
                                            {{ formatDefaultValue(fieldType.default_value) }}
                                        </div>
                                    </div>
                                    <div class="cms-reference-card-text">
                                        {{ fieldType.description }}
                                    </div>
                                    <div class="cms-reference-chip-list">
                                        <span
                                            v-for="attributeKey in fieldType.supported_attributes || []"
                                            :key="fieldType.key + '-' + attributeKey"
                                            class="cms-reference-chip"
                                        >
                                            {{ attributeKey }}
                                        </span>
                                        <span v-if="!fieldType.supported_attributes || fieldType.supported_attributes.length < 1" class="cms-reference-chip cms-reference-chip--muted">
                                            {{ $t('Keine Zusatzattribute') }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div v-else class="text-sm text-gray-500">
                                {{ $t('Keine Feldtypen geladen.') }}
                            </div>
                    </app-panel>

                    <app-panel :title="$t('Attribut-Bibliothek')">
                            <div v-if="customFieldAttributes.length > 0" class="cms-reference-card-grid cms-reference-card-grid--compact">
                                <div
                                    v-for="attribute in customFieldAttributes"
                                    :key="attribute.key"
                                    class="cms-reference-card"
                                >
                                    <div class="cms-reference-card-head">
                                        <div>
                                            <div class="cms-reference-card-title">{{ attribute.name }}</div>
                                            <div class="cms-reference-card-key">{{ attribute.key }}</div>
                                        </div>
                                        <div class="cms-reference-card-badge">
                                            {{ attribute.value_type }}
                                        </div>
                                    </div>
                                    <div class="cms-reference-card-text">
                                        {{ attribute.description }}
                                    </div>
                                </div>
                            </div>
                            <div v-else class="text-sm text-gray-500">
                                {{ $t('Keine Attributdefinitionen geladen.') }}
                            </div>
                    </app-panel>

                    <app-panel :title="$t('JSON-Beispiele')">
                            <div v-if="Object.keys(customFieldExamples).length > 0" class="cms-reference-card-grid">
                                <div
                                    v-for="(example, key) in customFieldExamples"
                                    :key="key"
                                    class="cms-reference-card cms-reference-card--code"
                                >
                                    <div class="cms-reference-card-title">{{ exampleCardLabel(key) }}</div>
                                    <pre class="cms-reference-code">{{ formatJson(example) }}</pre>
                                </div>
                            </div>
                            <div v-else class="text-sm text-gray-500">
                                {{ $t('Keine Beispieldefinitionen geladen.') }}
                            </div>
                    </app-panel>
                </div>
            </div>
        </form>

        <confirm-dialog
            :open="confirmDialog.open"
            :title="confirmDialog.title"
            :text="confirmDialog.text"
            :headline-id="'cms-module-type-confirm-modal-headline'"
            @close="closeConfirmDialog"
            @confirm="confirmDialogAction"
        />
    </main>
</template>

<script>
import HtmlCodeEditor from "@/components/forms/html-code-editor";
import CmsHtmlCodeEditor from "@/components/forms/cms-html-code-editor";
import ConfirmDialog from "@/components/modals/confirm-dialog";

export default {
    name: "cms-module-type-editor",
    components: {
        CmsHtmlCodeEditor,
        HtmlCodeEditor,
        ConfirmDialog,
    },
    data() {
        return {
            loading: false,
            activeTranslationIndex: 0,
            activeSchemaTab: 'settings_schema',
            activeCodeTab: 'module_css',
            languageOptions: [],
            confirmDialog: {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            },
            headerActionMenuOpen: false,
            moduleTypeStateResolved: false,
            schemaEditors: {
                settings_schema: '[]',
                translation_schema: '[]',
                slot_schema: '[]',
            },
            customFieldTypes: [],
            customFieldAttributes: [],
            customFieldExamples: {},
            moduleType: {
                key: '',
                name: '',
                icon: '',
                description: '',
                module_css: '',
                module_js: '',
                is_system: false,
                has_unpublished_changes: false,
                published_at: null,
                translations: [],
            },
        };
    },
    computed: {
        isEdit() {
            return !!this.$route.params.id;
        },
        activeTranslation() {
            return this.moduleType.translations[this.activeTranslationIndex] || null;
        },
        schemaTabs() {
            return [
                {key: 'settings_schema', label: this.$t('Settings')},
                {key: 'translation_schema', label: this.$t('Translation')},
                {key: 'slot_schema', label: this.$t('Slots')},
            ];
        },
        codeTabs() {
            return [
                {key: 'module_css', label: 'CSS'},
                {key: 'module_js', label: 'JS'},
            ];
        },
        activeSchemaInfo() {
            const schemaInfo = {
                settings_schema: {
                    title: this.$t('Settings'),
                    description: this.$t('Definiert sprachunabhängige Eingabefelder für die generelle Konfiguration des Moduls.'),
                    items: [
                        {
                            title: this.$t('Einsatzbereich'),
                            text: this.$t('Geeignet für Klassen, Schalter, Varianten, Zahlenwerte und andere globale Einstellungen.'),
                        },
                        {
                            title: this.$t('Struktur'),
                            text: this.$t('Jeder Eintrag beschreibt ein Formularfeld mit Key, Label, Typ, Standardwert und optionalen Attributen.'),
                        },
                    ],
                    note: this.$t('Die Werte stehen für das gesamte Modul zur Verfügung und bleiben über alle Sprachvarianten hinweg gleich.'),
                },
                translation_schema: {
                    title: this.$t('Translation'),
                    description: this.$t('Definiert sprachabhängige Eingabefelder für Inhalte, die je Sprache unterschiedlich gepflegt werden.'),
                    items: [
                        {
                            title: this.$t('Einsatzbereich'),
                            text: this.$t('Geeignet für Überschriften, Texte, Labels, HTML-Inhalte und andere lokalisierte Inhalte.'),
                        },
                        {
                            title: this.$t('Struktur'),
                            text: this.$t('Jeder Eintrag beschreibt ein sprachspezifisches Formularfeld und wird für jede aktivierte Sprache separat gespeichert.'),
                        },
                    ],
                    note: this.$t('Die Werte stehen innerhalb der jeweiligen Sprachvariante zur Verfügung und können im HTML gezielt ausgegeben werden.'),
                },
                slot_schema: {
                    title: this.$t('Slots'),
                    description: this.$t('Definiert verschachtelbare Innenbereiche für Kind-Module innerhalb des Content-Moduls.'),
                    items: [
                        {
                            title: this.$t('Einsatzbereich'),
                            text: this.$t('Geeignet für Slides, Menüpunkte, Akkordeon-Elemente oder andere wiederholbare Modulbereiche.'),
                        },
                        {
                            title: this.$t('Struktur'),
                            text: this.$t('Jeder Eintrag beschreibt einen Slot mit Key, Label sowie optionalen Grenzen und erlaubten Modultypen.'),
                        },
                    ],
                    note: this.$t(
                        'Die definierten Slot-Keys erscheinen rechts im HTML-Bereich und können dort über Platzhalter wie {placeholder} eingebunden werden.',
                        {placeholder: '{{ slot:slot_key }}'}
                    ),
                },
            };

            return schemaInfo[this.activeSchemaTab] || schemaInfo.settings_schema;
        },
        moduleSlotDefinitions() {
            const slotSchema = this.safeParseSchema(this.schemaEditors.slot_schema);

            return slotSchema
                .map((slot, index) => this.normalizeSlotDefinition(slot, index))
                .filter((slot) => slot !== null)
                .sort((left, right) => left.sort_order - right.sort_order);
        },
        publicationStateLabel() {
            if (!this.moduleType.published_at) {
                return this.$t('Noch nicht veröffentlicht');
            }

            if (this.moduleType.has_unpublished_changes) {
                return this.$t('Unveröffentlichte Änderungen');
            }

            return this.$t('Veröffentlicht');
        },
        publicationStateClass() {
            if (!this.moduleType.published_at) {
                return 'text-gray-500';
            }

            return this.moduleType.has_unpublished_changes ? 'text-amber-600' : 'text-green-600';
        },
        publicationStateDotClass() {
            if (!this.moduleType.published_at) {
                return 'bg-amber-500';
            }

            return this.moduleType.has_unpublished_changes ? 'bg-amber-500' : 'bg-green-500';
        },
        showPublicationStateDot() {
            return !this.isEdit || this.moduleTypeStateResolved;
        },
    },
    mounted() {
        document.addEventListener('click', this.handleDocumentClick);
        Promise.allSettled([
            this.loadOptions(),
            this.loadCustomFieldLibrary(),
        ]).finally(() => {
            if (this.isEdit) {
                this.getModuleType();
            } else {
                this.syncTranslations();
                this.syncSchemaEditors();
            }
        });
    },
    beforeDestroy() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    methods: {
        openModuleTypeHtmlMediaDrawer() {
            this.$refs.moduleTypeHtmlEditor?.openMediaDrawer();
        },
        closeHeaderActionMenu() {
            this.headerActionMenuOpen = false;
        },
        toggleHeaderActionMenu() {
            this.headerActionMenuOpen = !this.headerActionMenuOpen;
        },
        runHeaderAction(action) {
            this.closeHeaderActionMenu();

            if (action === 'publish') {
                this.publishModuleType();
                return;
            }

            if (action === 'reset') {
                this.resetModuleTypeDraft();
                return;
            }

            if (action === 'cancel') {
                this.$router.push('/workspace/cms/content-modules');
                return;
            }

            if (action === 'delete') {
                this.deleteModuleType();
            }
        },
        handleDocumentClick(event) {
            if (!this.headerActionMenuOpen) {
                return;
            }

            const menuElement = this.$refs.headerActionMenu;
            const triggerElement = this.$refs.headerActionMenuTrigger;
            const wrapperElement = this.$refs.headerActionMenuWrapper;

            if (!(menuElement && menuElement.contains(event.target))
                && !(triggerElement && triggerElement.contains(event.target))
                && !(wrapperElement && wrapperElement.contains(event.target))) {
                this.closeHeaderActionMenu();
            }
        },
        loadOptions() {
            return axios.get('api/workspace/admin/cms/module-types/options').then((response) => {
                this.languageOptions = Array.isArray(response.data.languages) ? response.data.languages : [];
            });
        },
        loadCustomFieldLibrary() {
            return axios.get('api/workspace/admin/cms/custom-fields/library').then((response) => {
                this.customFieldTypes = Array.isArray(response.data.field_types) ? response.data.field_types : [];
                this.customFieldAttributes = Array.isArray(response.data.field_attributes) ? response.data.field_attributes : [];
                this.customFieldExamples = response.data.schema_examples || {};
            }).catch(() => {
                this.customFieldTypes = [];
                this.customFieldAttributes = [];
                this.customFieldExamples = {};
            });
        },
        getModuleType() {
            this.moduleTypeStateResolved = false;
            this.loading = true;
            axios.get('api/workspace/admin/cms/module-types/' + this.$route.params.id).then((response) => {
                this.moduleType = response.data;
                this.syncTranslations();
                this.syncSchemaEditors();
                this.moduleTypeStateResolved = true;
                this.loading = false;
            }).catch(() => {
                this.moduleTypeStateResolved = true;
                this.loading = false;
            });
        },
        saveModuleType() {
            const payload = this.buildPayload();
            if (!payload) {
                return Promise.resolve(null);
            }

            const request = this.isEdit
                ? axios.put('api/workspace/admin/cms/module-types/' + this.$route.params.id, payload)
                : axios.post('api/workspace/admin/cms/module-types', payload);

            return request.then((response) => {
                const moduleTypeId = response.data.module_type.id;
                this.moduleType = response.data.module_type;
                this.syncTranslations();
                this.syncSchemaEditors();
                this.moduleTypeStateResolved = true;

                if (!this.isEdit) {
                    this.$router.push('/workspace/cms/content-modules/' + moduleTypeId + '/edit');
                }

                this.$notify({title: this.$t('Success'), text: this.isEdit ? this.$t('Entwurf gespeichert') : this.$t('Preset angelegt'), type: 'success'});
                return response;
            });
        },
        publishModuleType() {
            this.saveModuleType().then((response) => {
                if (!response) {
                    return;
                }

                axios.post('api/workspace/admin/cms/module-types/' + this.$route.params.id + '/publish').then((publishResponse) => {
                    this.moduleType = publishResponse.data.module_type;
                    this.syncTranslations();
                    this.syncSchemaEditors();
                    this.moduleTypeStateResolved = true;
                    this.$notify({title: this.$t('Success'), text: this.$t('Preset erfolgreich veröffentlicht'), type: 'success'});
                });
            });
        },
        resetModuleTypeDraft() {
            this.openConfirmDialog({
                title: this.$t('Auf veröffentlichte Version zurücksetzen'),
                text: this.$t('Soll der aktuelle Entwurf wirklich auf die zuletzt veröffentlichte Version zurückgesetzt werden? Nicht veröffentlichte Änderungen gehen dabei verloren.'),
                pendingAction: () => {
                    axios.post('api/workspace/admin/cms/module-types/' + this.$route.params.id + '/reset').then((response) => {
                        this.moduleType = response.data.module_type;
                        this.syncTranslations();
                        this.syncSchemaEditors();
                        this.moduleTypeStateResolved = true;
                        this.$notify({title: this.$t('Success'), text: this.$t('Entwurf auf veröffentlichte Version zurückgesetzt'), type: 'success'});
                    });
                },
            });
        },
        deleteModuleType() {
            this.openConfirmDialog({
                title: this.$t('Preset löschen'),
                text: this.$t('Soll dieses Content-Modul wirklich gelöscht werden?'),
                pendingAction: () => {
                    axios.delete('api/workspace/admin/cms/module-types/' + this.$route.params.id).then(() => {
                        this.$notify({title: this.$t('Success'), text: this.$t('Data deleted successfully'), type: 'success'});
                        this.$router.push('/workspace/cms/content-modules');
                    });
                },
            });
        },
        buildPayload() {
            const settingsSchema = this.parseJsonEditor(this.schemaEditors.settings_schema, this.$t('Settings-Schema'));
            const translationSchema = this.parseJsonEditor(this.schemaEditors.translation_schema, this.$t('Translation-Schema'));
            const slotSchema = this.parseJsonEditor(this.schemaEditors.slot_schema, this.$t('Slot-Schema'));

            if (settingsSchema === null || translationSchema === null || slotSchema === null) {
                return null;
            }

            return {
                key: String(this.moduleType.key || '').trim(),
                name: String(this.moduleType.name || '').trim(),
                icon: String(this.moduleType.icon || '').trim(),
                description: String(this.moduleType.description || '').trim(),
                module_css: this.moduleType.module_css || '',
                module_js: this.moduleType.module_js || '',
                settings_schema: settingsSchema,
                translation_schema: translationSchema,
                slot_schema: slotSchema,
                translations: this.moduleType.translations.map((translation) => ({
                    id: translation.id,
                    cms_language_id: translation.cms_language_id,
                    module_html: translation.module_html || '',
                })),
            };
        },
        parseJsonEditor(source, label) {
            try {
                const parsed = JSON.parse(String(source || '[]'));
                if (!Array.isArray(parsed)) {
                    this.$notify({
                        title: this.$t('Error'),
                        text: this.$t('Der Bereich {label} muss ein JSON-Array enthalten.', {label}),
                        type: 'error'
                    });
                    return null;
                }

                return parsed;
            } catch (error) {
                this.$notify({
                    title: this.$t('Error'),
                    text: this.$t('Der Bereich {label} enthält kein gültiges JSON.', {label}),
                    type: 'error'
                });
                return null;
            }
        },
        syncSchemaEditors() {
            this.schemaEditors = {
                settings_schema: this.formatJson(this.moduleType.settings_schema || []),
                translation_schema: this.formatJson(this.moduleType.translation_schema || []),
                slot_schema: this.formatJson(this.moduleType.slot_schema || []),
            };
        },
        formatJson(value) {
            const normalized = value === undefined || value === null ? [] : value;
            return JSON.stringify(normalized, null, 2);
        },
        formatDefaultValue(value) {
            if (Array.isArray(value) || (value && typeof value === 'object')) {
                return JSON.stringify(value);
            }

            if (typeof value === 'boolean') {
                return value ? 'true' : 'false';
            }

            if (value === null || value === undefined || value === '') {
                return this.$t('Standard');
            }

            return String(value);
        },
        exampleCardLabel(key) {
            const labels = {
                settings_field: this.$t('Settings-Feld'),
                translation_field: this.$t('Translation-Feld'),
                slot: this.$t('Slot'),
            };

            return labels[key] || key;
        },
        safeParseSchema(source) {
            try {
                const parsed = JSON.parse(String(source || '[]'));
                return Array.isArray(parsed) ? parsed : [];
            } catch (error) {
                return Array.isArray(this.moduleType.slot_schema) ? this.moduleType.slot_schema : [];
            }
        },
        normalizeSlotDefinition(slot, index) {
            if (!slot || typeof slot !== 'object') {
                return null;
            }

            const key = String(slot.key || '').trim();
            if (key === '') {
                return null;
            }

            return {
                key,
                label: String(slot.label || key).trim(),
                description: String(slot.description || '').trim(),
                max_items: Number.isFinite(Number(slot.max_items)) && Number(slot.max_items) > 0
                    ? Number(slot.max_items)
                    : null,
                sort_order: Number.isFinite(Number(slot.sort_order)) ? Number(slot.sort_order) : index,
            };
        },
        syncTranslations() {
            if (!Array.isArray(this.languageOptions) || this.languageOptions.length < 1) {
                this.moduleType.translations = [];
                this.activeTranslationIndex = 0;
                return;
            }

            const existingByLanguageId = new Map(
                (Array.isArray(this.moduleType.translations) ? this.moduleType.translations : []).map((translation) => [
                    translation.cms_language_id,
                    translation,
                ])
            );

            this.moduleType.translations = this.languageOptions.map((language) => {
                const existing = existingByLanguageId.get(language.id);

                return {
                    id: existing?.id || null,
                    cms_language_id: language.id,
                    module_html: existing?.module_html || '',
                    published_module_html: existing?.published_module_html || '',
                    language: {
                        id: language.id,
                        locale: language.locale,
                        name: language.name,
                        is_default: !!language.is_default,
                    },
                };
            });

            this.activeTranslationIndex = Math.max(0, Math.min(this.activeTranslationIndex, this.moduleType.translations.length - 1));
        },
        translationLabel(translation, index) {
            const language = translation.language || this.languageOptions.find((item) => item.id === translation.cms_language_id);
            return language ? language.locale : `${this.$t('Sprache')} ${index + 1}`;
        },
        openConfirmDialog({title, text, pendingAction}) {
            this.confirmDialog = {
                open: true,
                title,
                text,
                pendingAction,
            };
        },
        closeConfirmDialog() {
            this.confirmDialog = {
                open: false,
                title: '',
                text: '',
                pendingAction: null,
            };
        },
        confirmDialogAction() {
            const action = this.confirmDialog.pendingAction;
            this.closeConfirmDialog();
            if (typeof action === 'function') {
                action();
            }
        },
    },
}
</script>

<style scoped>
.cms-translation-panel-body {
    padding: 0 !important;
    overflow: hidden;
}

.cms-translation-content {
    padding: 0;
}

.cms-translation-html-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    min-height: 30rem;
    align-items: stretch;
}

.cms-translation-html-editor {
    min-height: 30rem;
    padding: 0 !important;
    overflow: hidden;
}

.cms-variable-column {
    display: flex;
    flex-direction: column;
    min-height: 0;
    border-left: 1px solid rgb(243, 244, 246);
    background: #ffffff;
}

.cms-variable-column-body {
    flex: 1 1 auto;
    min-height: 0;
    padding: 0 !important;
    overflow: hidden;
}

.cms-variable-section + .cms-variable-section {
    border-top: 1px solid rgb(243, 244, 246);
}

.cms-variable-section-title {
    padding: 1rem 1rem 0.75rem;
    font-size: 0.75rem;
    font-weight: 700;
    line-height: 1rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgb(107, 114, 128);
}

.cms-variable-row {
    padding: 0.875rem 1rem;
    border-top: 1px solid rgb(243, 244, 246);
}

.cms-slot-badge {
    flex-shrink: 0;
    border: 1px solid rgb(229, 231, 235);
    border-radius: 9999px;
    padding: 0.2rem 0.55rem;
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1rem;
    color: rgb(75, 85, 99);
    background: rgb(249, 250, 251);
}

.cms-variable-empty {
    padding: 0 1rem 1rem;
    font-size: 0.75rem;
    line-height: 1rem;
    color: rgb(107, 114, 128);
}

.cms-code-panel-body {
    padding: 0 !important;
    height: 34rem;
    overflow: hidden;
}

.cms-schema-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 320px;
    height: 100%;
    min-height: 34rem;
    align-items: stretch;
}

.cms-schema-sidebar {
    display: flex;
    flex-direction: column;
    min-height: 0;
    border-left: 1px solid rgb(243, 244, 246);
    background: #ffffff;
}

.cms-schema-sidebar-body {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.cms-schema-sidebar-text {
    padding: 0 1rem 1rem;
    font-size: 0.75rem;
    line-height: 1.15rem;
    color: rgb(107, 114, 128);
}

.cms-code-panel-shell {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.cms-code-panel-fill {
    height: 100%;
    min-height: 0;
    flex: 1 1 auto;
}

.cms-reference-card-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
    gap: 1rem;
}

.cms-reference-card-grid--compact {
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
}

.cms-reference-card {
    display: flex;
    flex-direction: column;
    gap: 0.875rem;
    min-width: 0;
    border: 1px solid rgb(229, 231, 235);
    border-radius: 1rem;
    padding: 1rem;
    background: rgb(249, 250, 251);
}

.cms-reference-card--code {
    background: #ffffff;
}

.cms-reference-card-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.cms-reference-card-title {
    font-size: 0.9375rem;
    font-weight: 600;
    line-height: 1.35rem;
    color: rgb(17, 24, 39);
}

.cms-reference-card-key {
    margin-top: 0.25rem;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 0.75rem;
    line-height: 1rem;
    color: rgb(107, 114, 128);
}

.cms-reference-card-badge {
    flex-shrink: 0;
    border: 1px solid rgb(229, 231, 235);
    border-radius: 9999px;
    padding: 0.2rem 0.6rem;
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1rem;
    color: rgb(75, 85, 99);
    background: #ffffff;
}

.cms-reference-card-text {
    font-size: 0.8125rem;
    line-height: 1.3rem;
    color: rgb(75, 85, 99);
}

.cms-reference-chip-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.cms-reference-chip {
    border-radius: 9999px;
    padding: 0.3rem 0.65rem;
    background: rgb(229, 231, 235);
    font-size: 0.6875rem;
    font-weight: 600;
    line-height: 1rem;
    color: rgb(55, 65, 81);
}

.cms-reference-chip--muted {
    background: rgb(243, 244, 246);
    color: rgb(107, 114, 128);
}

.cms-reference-code {
    margin: 0;
    border: 1px solid rgb(229, 231, 235);
    border-radius: 0.875rem;
    padding: 1rem;
    background: rgb(248, 250, 252);
    font-size: 0.75rem;
    line-height: 1.15rem;
    color: rgb(31, 41, 55);
    overflow: auto;
}

@media (max-width: 1279px) {
    .cms-translation-html-layout {
        grid-template-columns: minmax(0, 1fr);
    }

    .cms-variable-column {
        border-left: 0;
        border-top: 1px solid rgb(243, 244, 246);
    }

    .cms-schema-layout {
        grid-template-columns: minmax(0, 1fr);
    }

    .cms-schema-sidebar {
        border-left: 0;
        border-top: 1px solid rgb(243, 244, 246);
    }
}
</style>
